import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { LoginPageSEO } from '../components/SEO';

// Google icon component
const GoogleIcon = () => (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
        <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
        />
        <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
        />
        <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
        />
        <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
        />
    </svg>
);

// Apple icon component (for future use)
const AppleIcon = () => (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
);

export const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [magicLinkSent, setMagicLinkSent] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [appleLoading, setAppleLoading] = useState(false);
    const appleInitialized = useRef(false);
    
    const { requestMagicLink, initiateGoogleLogin, handleAppleCallback } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Check for error from OAuth callback
    const locationError = location.state?.error;

    // Initialize Apple Sign in with Apple JS SDK
    useEffect(() => {
        const initApple = () => {
            if (!window.AppleID || appleInitialized.current) return;
            try {
                const redirectURI = import.meta.env.VITE_APPLE_REDIRECT_URI
                    || `${window.location.origin}/auth/apple/callback`;
                window.AppleID.auth.init({
                    clientId: import.meta.env.VITE_APPLE_CLIENT_ID,
                    redirectURI,
                    scope: 'name email',
                    usePopup: true,
                });
                appleInitialized.current = true;
            } catch (e) {
                console.warn('Apple auth init failed:', e);
            }
        };

        // If SDK already loaded
        if (window.AppleID) {
            initApple();
        } else {
            // Wait for the script to load (added in index.html)
            const handler = () => initApple();
            document.addEventListener('AppleIDSignInOnSuccess', handler);
            window.addEventListener('load', initApple);
            return () => {
                document.removeEventListener('AppleIDSignInOnSuccess', handler);
                window.removeEventListener('load', initApple);
            };
        }
    }, []);

    const handleMagicLink = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Store remember me preference for after magic link verification
        if (rememberMe) {
            localStorage.setItem('remember_me_pending', 'true');
        }

        const result = await requestMagicLink(email);
        setLoading(false);

        if (result.success) {
            setMagicLinkSent(true);
        } else {
            setError(result.error);
        }
    };

    const handleGoogleLogin = () => {
        // Store remember me preference for after OAuth callback
        if (rememberMe) {
            localStorage.setItem('remember_me_pending', 'true');
        }
        initiateGoogleLogin();
    };

    const handleAppleLogin = async () => {
        if (!window.AppleID) {
            setError('Apple Sign In is not available. Please refresh and try again.');
            return;
        }
        // Re-init if needed (Safari can unload the SDK)
        if (!appleInitialized.current) {
            try {
                const redirectURI = import.meta.env.VITE_APPLE_REDIRECT_URI
                    || `${window.location.origin}/auth/apple/callback`;
                window.AppleID.auth.init({
                    clientId: import.meta.env.VITE_APPLE_CLIENT_ID,
                    redirectURI,
                    scope: 'name email',
                    usePopup: true,
                });
                appleInitialized.current = true;
            } catch (e) {
                setError('Failed to initialize Apple Sign In.');
                return;
            }
        }
        setError('');
        setAppleLoading(true);
        try {
            const response = await window.AppleID.auth.signIn();
            const { authorization, user: appleUser } = response;
            if (rememberMe) localStorage.setItem('remember_me_pending', 'true');
            const result = await handleAppleCallback(
                authorization.id_token,
                authorization.code,
                appleUser || null,
            );
            if (result.success) {
                const redirectTo = location.state?.from?.pathname || '/library';
                navigate(redirectTo, { replace: true });
            } else {
                setError(result.error || 'Apple authentication failed');
            }
        } catch (err) {
            // User closed the popup — not an error
            if (err?.error === 'popup_closed_by_user' || err?.error === 'user_cancelled_authorize') {
                // silently ignore
            } else {
                setError('Apple Sign In failed. Please try again.');
                console.error('Apple auth error:', err);
            }
        } finally {
            setAppleLoading(false);
        }
    };

    // Magic link sent confirmation screen
    if (magicLinkSent) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex" data-testid="login-page">
                <LoginPageSEO />
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="w-full max-w-md">
                        <Link 
                            to="/" 
                            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to Home
                        </Link>

                        <Card className="border-border/60">
                            <CardHeader className="space-y-1 text-center">
                                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                                    <Mail className="h-8 w-8 text-green-600" />
                                </div>
                                <CardTitle className="font-serif text-2xl">Check your email</CardTitle>
                                <CardDescription>
                                    We sent a magic link to <strong>{email}</strong>
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <p className="text-center text-sm text-muted-foreground mb-4">
                                    Click the link in your email to sign in. The link expires in 30 minutes.
                                </p>
                                <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => {
                                        setMagicLinkSent(false);
                                        setEmail('');
                                    }}
                                >
                                    Back to Login
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <div className="hidden lg:block w-1/2 relative">
                    <img
                        src="https://images.unsplash.com/photo-1752649937956-0859c4446377?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                        alt="Artist studio"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-background/50 to-transparent" />
                </div>
            </div>
        );
    }

    // Main login screen - Desktop-first centered layout
    return (
        <div className="min-h-[calc(100vh-64px)] flex" data-testid="login-page">
            <LoginPageSEO />
            {/* Left: Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
                        data-testid="back-to-home"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Home
                    </Link>

                    <Card className="border-border/60">
                        <CardHeader className="space-y-1 text-center pb-4">
                            <CardTitle className="font-serif text-2xl">
                                Sign in to Zarolla
                            </CardTitle>
                            <CardDescription>
                                Access your video library and courses
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Error message */}
                            {(error || locationError) && (
                                <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm" data-testid="login-error">
                                    {error || locationError}
                                </div>
                            )}

                            {/* Google OAuth Button - Primary */}
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full h-12 text-base font-medium border-2 hover:bg-secondary/80"
                                onClick={handleGoogleLogin}
                                data-testid="google-login-btn"
                            >
                                <GoogleIcon />
                                <span className="ml-3">Continue with Google</span>
                            </Button>

                            {/* Apple Sign In Button */}
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full h-12 text-base font-medium border-2 hover:bg-secondary/80"
                                onClick={handleAppleLogin}
                                disabled={appleLoading}
                                data-testid="apple-login-btn"
                            >
                                {appleLoading ? (
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                ) : (
                                    <AppleIcon />
                                )}
                                <span className="ml-3">
                                    {appleLoading ? 'Signing in...' : 'Continue with Apple'}
                                </span>
                            </Button>

                            {/* Divider */}
                            <div className="relative py-2">
                                <div className="absolute inset-0 flex items-center">
                                    <Separator className="w-full" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-card px-3 text-muted-foreground">or</span>
                                </div>
                            </div>

                            {/* Magic Link Form */}
                            <form onSubmit={handleMagicLink} className="space-y-4" aria-labelledby="magic-link-heading">
                                <h3 id="magic-link-heading" className="sr-only">Sign in with email</h3>
                                <div className="space-y-2">
                                    <label htmlFor="login-email" className="sr-only">
                                        Email address
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                        <Input
                                            id="login-email"
                                            type="email"
                                            placeholder="Enter your email address"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            className="pl-10 h-12"
                                            data-testid="email-input"
                                            aria-describedby="email-hint"
                                            autoComplete="email"
                                        />
                                    </div>
                                    <p id="email-hint" className="sr-only">
                                        We'll send you a secure link to sign in
                                    </p>
                                </div>

                                <Button
                                    type="submit"
                                    variant="outline"
                                    className="w-full h-12 text-base font-medium border-2 hover:bg-accent hover:text-accent-foreground hover:border-accent"
                                    disabled={loading || !email}
                                    data-testid="magic-link-btn"
                                    aria-busy={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                                            <span>Sending link...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
                                            Email me a login link
                                        </>
                                    )}
                                </Button>
                            </form>

                            {/* Remember Me */}
                            <div className="flex items-center space-x-2 pt-2">
                                <Checkbox 
                                    id="remember-me" 
                                    checked={rememberMe}
                                    onCheckedChange={setRememberMe}
                                    data-testid="remember-me-checkbox"
                                />
                                <label 
                                    htmlFor="remember-me" 
                                    className="text-sm text-muted-foreground cursor-pointer"
                                >
                                    Remember this device
                                </label>
                            </div>

                            {/* Help text */}
                            <p className="text-center text-xs text-muted-foreground pt-2">
                                No password required. We&apos;ll send you a secure link to sign in.
                            </p>
                            
                            {/* Forgot Password link */}
                            <p className="text-center text-xs text-muted-foreground">
                                Already have a password?{' '}
                                <Link to="/forgot-password" className="text-accent hover:underline" data-testid="forgot-password-link">
                                    Forgot Password?
                                </Link>
                            </p>
                        </CardContent>
                    </Card>

                    <p className="text-center text-sm text-muted-foreground mt-6">
                        By signing in, you agree to our{' '}
                        <Link to="/terms" className="text-accent hover:underline">
                            Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-accent hover:underline">
                            Privacy Policy
                        </Link>
                    </p>
                </div>
            </div>

            {/* Right: Image (desktop only) */}
            <div className="hidden lg:block w-1/2 relative">
                <img
                    src="https://images.unsplash.com/photo-1752649937956-0859c4446377?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                    alt="Artist studio"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-background/50 to-transparent" />
            </div>
        </div>
    );
};

export default LoginPage;
